import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Header from '../../../components/Header';
import ComponentHeader from '../../../components/ComponentHeader';
import SpecificInstructionTable from './SpecificInstructionTable';
import AddSpecificInstructionModal from './AddSpecificInstructionModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import { X, AlertCircle } from 'lucide-react';

const SpecificInstruction = () => {
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState<any | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');

  // Get user role from Redux
  const role = useSelector((state: any) => state.user?.role);
  const isSystemEngineer = role === 'system_engineer';

  const handleEdit = (data: any) => {
    setEditData(data);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditData(null);
  };

  const showSuccessMessage = (title: string, message: string) => {
    setSuccessMessage({ title, message });
    setSuccessModal(true);
  };

  const handleSuccess = () => {
    setShowModal(false);
    const isEdit = !!editData;
    setEditData(null);
    setRefreshTrigger((prev) => prev + 1);

    if (isEdit) {
      showSuccessMessage('Specific Instruction Updated', 'The Specific Instruction has been successfully updated.');
    } else {
      showSuccessMessage('Specific Instruction Created', 'Specific Instruction added successfully.');
    }
  };

  //  Handle Delete Success
  const handleDeleteSuccess = () => {
    showSuccessMessage('Specific Instruction Deleted', 'The Specific Instruction has been successfully deleted.');
  };

  //  ADDED: receives debounced query from ComponentHeader
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <Header title={'Specific Instruction'} />

      <div className="flex-1 overflow-auto p-4">
        <div className="flex items-center justify-between mb-2">
          {isSystemEngineer && (
            <button
              onClick={() => {
                setEditData(null);
                setShowModal(true);
              }}
              className="bg-red-600 text-white font-semibold px-4 py-2 rounded hover:bg-red-700 transition whitespace-nowrap"
            >
              + Specific Instruction
            </button>
          )}

          <div className="ml-auto">
            <ComponentHeader onSearch={handleSearch} />
          </div>
        </div>

        {/* Error Toast */}
        {errorMessage && (
          <div className="fixed bottom-6 right-6 z-[100] animate-slide-up">
            <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-start gap-3 min-w-[320px] max-w-md">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Error</h3>
                <p className="text-sm">{errorMessage}</p>
              </div>
              <button onClick={() => setErrorMessage(null)} className="flex-shrink-0 hover:bg-red-600 rounded p-1 transition-colors">
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        <SpecificInstructionTable
          onEdit={handleEdit}
          refreshTrigger={refreshTrigger}
          onDeleteSuccess={handleDeleteSuccess}
          onError={(message) => {
            setErrorMessage(message);
            setTimeout(() => setErrorMessage(null), 5000);
          }}
          isSystemEngineer={isSystemEngineer}
          searchQuery={searchQuery} //  ADDED: pass searchQuery to table
        />
      </div>

      {showModal && (
        <AddSpecificInstructionModal
          onClose={handleCloseModal}
          editData={editData}
          onSuccess={handleSuccess}
          onError={(message) => {
            setErrorMessage(message);
            setTimeout(() => setErrorMessage(null), 5000);
          }}
        />
      )}

      <SuccessModal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
        title={successMessage.title}
        message={successMessage.message}
        buttonText="OK"
        onButtonClick={() => setSuccessModal(false)}
      />
    </div>
  );
};

export default SpecificInstruction;
